/**
 * Motor de sesión: refresh silencioso, metadatos y eventos globales.
 */

const REFRESH_LEAD_MS = 2 * 60 * 1000; // renovar access ~2 min antes de expirar

let sessionMeta = null;
let sessionStatus = 'active'; // active | warning | expired
let refreshTimer = null;
let warningTimer = null;
let refreshPromise = null;
// **La función que renueva, registrada una sola vez** (corrección del 2026-10-14). El temporizador
// que renueva el acceso 2 minutos antes de que venza llamaba a `performRefresh()` sin pasarle
// ninguna función: tiraba `refreshFn is not a function`, el `.catch` lo tragaba, y la renovación
// anticipada **nunca corrió**. Cada 16 minutos, en cada pestaña abierta, un pedido salía sin
// credencial, el servidor contestaba 401 y recién ahí el interceptor renovaba y reintentaba. La
// pantalla andaba; el log de diagnóstico se llenaba de «Not authenticated». Diego lo vio ahí.
let refreshFnRegistrada = null;

const listeners = new Set();

function emit(event, payload) {
  listeners.forEach((fn) => {
    try {
      fn(event, payload);
    } catch (_) {
      /* ignore */
    }
  });
}

export function subscribeSession(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSessionMeta() {
  return sessionMeta;
}

export function getSessionStatus() {
  return sessionStatus;
}

function parseMs(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

function computeStatus(meta) {
  if (!meta) return 'active';
  const refreshMs = parseMs(meta.refresh_expires_at);
  if (!refreshMs) return 'active';
  const warningMinutes = meta.session_warning_minutes ?? 30;
  const warningMs = warningMinutes * 60 * 1000;
  const now = Date.now();
  if (refreshMs <= now) return 'expired';
  if (refreshMs - now <= warningMs) return 'warning';
  return 'active';
}

function clearTimers() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (warningTimer) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }
}

function scheduleFromMeta(meta) {
  clearTimers();
  if (!meta) return;

  const accessMs = parseMs(meta.access_expires_at);
  const refreshMs = parseMs(meta.refresh_expires_at);
  const now = Date.now();

  if (accessMs && accessMs > now) {
    const delay = Math.max(5000, accessMs - now - REFRESH_LEAD_MS);
    refreshTimer = setTimeout(() => {
      // Si falla, el próximo pedido recibe 401 y el interceptor reintenta: no se pierde nada.
      performRefresh().catch((err) => {
        console.warn('No se pudo renovar la sesión por adelantado:', err?.message || err);
      });
    }, delay);
  }

  if (refreshMs && refreshMs > now) {
    const warningMinutes = meta.session_warning_minutes ?? 30;
    const warnAt = refreshMs - warningMinutes * 60 * 1000;
    const warnDelay = warnAt - now;
    if (warnDelay <= 0) {
      setSessionStatus('warning');
    } else {
      warningTimer = setTimeout(() => setSessionStatus('warning'), warnDelay);
    }
  }
}

function setSessionStatus(next) {
  if (sessionStatus === next) return;
  sessionStatus = next;
  emit('status', sessionStatus);
}

export function applySessionMeta(meta) {
  sessionMeta = meta || null;
  const next = computeStatus(sessionMeta);
  setSessionStatus(next === 'expired' ? 'expired' : next);
  scheduleFromMeta(sessionMeta);
  emit('meta', sessionMeta);
}

export function clearSessionMeta() {
  sessionMeta = null;
  clearTimers();
  setSessionStatus('active');
  emit('meta', null);
}

export function notifySessionExpired() {
  clearTimers();
  sessionMeta = null;
  setSessionStatus('expired');
  emit('session-expired');
}

function shouldAttemptRefresh(url, config) {
  const u = String(url || '');
  if (config?._skipAuthRefresh) return false;
  if (u.includes('/auth/login')) return false;
  // Un código mal tipeado devuelve 401 y **no** es una sesión vencida: no hay sesión todavía.
  if (u.includes('/auth/segundo-factor')) return false;
  if (u.includes('/auth/refresh')) return false;
  if (u.includes('/auth/register')) return false;
  return true;
}

export function createAuthRefreshInterceptor(api, refreshFn) {
  refreshFnRegistrada = refreshFn;
  api.interceptors.response.use(
    (response) => {
      const session = response?.data?.session;
      if (session && typeof session === 'object') {
        applySessionMeta(session);
      }
      return response;
    },
    async (error) => {
      const config = error.config || {};
      const status = error.response?.status;
      const url = String(config.url || '');

      if (status !== 401 || !shouldAttemptRefresh(url, config) || config._authRetried) {
        return Promise.reject(error);
      }

      try {
        await performRefresh(refreshFn);
        config._authRetried = true;
        return api.request(config);
      } catch (refreshErr) {
        // Bootstrap without cookies: me()+refresh 401 is expected; do not block login UI.
        if (sessionMeta) {
          notifySessionExpired();
        }
        return Promise.reject(refreshErr);
      }
    }
  );
}

export async function performRefresh(refreshFn = refreshFnRegistrada) {
  if (refreshPromise) return refreshPromise;
  if (typeof refreshFn !== 'function') {
    throw new Error('No hay función de renovación de sesión registrada');
  }

  refreshPromise = (async () => {
    const res = await refreshFn();
    const session = res?.data?.session;
    if (session) {
      applySessionMeta(session);
    }
    return res;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export function extendSession(refreshFn) {
  return performRefresh(refreshFn);
}

export function minutesUntilRefreshExpiry() {
  const refreshMs = parseMs(sessionMeta?.refresh_expires_at);
  if (!refreshMs) return null;
  return Math.max(0, Math.ceil((refreshMs - Date.now()) / 60000));
}
